// eslint-disable-next-line max-classes-per-file
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { LakehouseMaintenanceActivity } from 'app/model/pipeline/lakehouse-maintenance.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { IModel, isLogicAppExpression, LakehouseLinkedService, LinkedService, Owned, ParameterConfig, ReferenceAdapter } from 'shell-models-lib';
import { linkedServiceReferenceToResource } from 'app/shared/utils/linkedservice-parameter.util';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { LoggerService } from 'app/logger/logger.service';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { LinkedServiceAdapter } from '../../linkedservices/linkedservice.adapter';
import { ActivityAsyncAdapter } from '../activity.adapter';
import { ILHMActivityContext } from 'app/interfaces/copilot.pipeline.action.types';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(LakehouseMaintenanceActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: '*'
    }
})
export class LakehouseMaintenanceActivityAdapter extends ActivityAsyncAdapter<LakehouseMaintenanceActivity, Model.ExecutionActivity, ILHMActivityContext>  {

    protected override async _toResource(activity: LakehouseMaintenanceActivity): Promise<Model.ExecutionActivity> {
        const loggerService = await getServiceInstance<LoggerService>(this.context.contextData, 'LoggerService');

        const modelActivity: Model.ExecutionActivity = super._getCommonActivityProperties(
            activity,
            Metamodel.modelOf(activity).name
        ) as Model.ExecutionActivity;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties = {} as any;
        typeProperties.schema = ValueAdapter.toResource(activity, LakehouseMaintenanceActivity.SCHEMA_NAME);
        typeProperties.table = ValueAdapter.toResource(activity, LakehouseMaintenanceActivity.TABLE_NAME);

        const optimizeEnabled = activity.optimizeEnabled || ValueAdapter.isTemplated(activity, LakehouseMaintenanceActivity.OPTIMIZE_ENABLED);
        if (optimizeEnabled) {
            typeProperties.optimizeEnabled = ValueAdapter.toResource(activity, LakehouseMaintenanceActivity.OPTIMIZE_ENABLED);
            typeProperties.vOrderEnabled = ValueAdapter.toResource(activity, LakehouseMaintenanceActivity.VORDER_ENABLED);

            typeProperties.zOrderEnabled = ValueAdapter.toResource(activity, LakehouseMaintenanceActivity.ZORDER_ENABLED);
            const zOrderEnabled = activity.zOrderEnabled || ValueAdapter.isTemplated(activity, LakehouseMaintenanceActivity.ZORDER_ENABLED);
            if (zOrderEnabled) {
                typeProperties.zOrderColumns = activity.zOrderColumns.map((col) => col.name);
            }
        }

        typeProperties.vacuumEnabled = ValueAdapter.toResource(activity, LakehouseMaintenanceActivity.VACUUM_ENABLED);
        const vacuumEnabled = activity.vacuumEnabled || ValueAdapter.isTemplated(activity, LakehouseMaintenanceActivity.VACUUM_ENABLED);
        if (vacuumEnabled) {
            typeProperties.retentionPeriod = ValueAdapter.toResource(activity, LakehouseMaintenanceActivity.RETENTION_PERIOD);
        }

        typeProperties.waitOnCompletion = ValueAdapter.toResource(activity, LakehouseMaintenanceActivity.WAIT_ON_COMPLETION);

        let linkedService: Model.LinkedServiceResource = undefined;
        if (activity.lakehouseLinkedService) {
            const linkedServiceAdapter = this.context.asyncAdapterFor(Metamodel.modelOf(activity.lakehouseLinkedService));
            linkedService = await linkedServiceAdapter.toResource(
                activity.lakehouseLinkedService
            );

            // NOTE anredd: Below line results in external references property being added
            if (activity.artifactDmtsCredential) {
                linkedService.properties.linkedServiceName = linkedServiceReferenceToResource(
                    activity.artifactDmtsCredential,
                    this.context,
                    loggerService
                );
            }
        }
        modelActivity.connectionSettings = linkedService;
        modelActivity.typeProperties = typeProperties;

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelActivity, parsed);
        }

        return modelActivity;
    }

    protected override async _toModel(modelActivity: Model.ExecutionActivity): Promise<LakehouseMaintenanceActivity> {
        const loggerService = await getServiceInstance<LoggerService>(this.context.contextData, 'LoggerService');

        const metaModel = Metamodel.modelByName(modelActivity.type);
        const activity = metaModel.create<LakehouseMaintenanceActivity>(modelActivity.name); 


        this._updatedCommonProperties(modelActivity, activity);

        const typeProperties = modelActivity.typeProperties as Model.LakehouseMaintenanceActivityTypeProperties;

        ValueAdapter.toModel(activity, LakehouseMaintenanceActivity.SCHEMA_NAME, typeProperties.schema);
        ValueAdapter.toModel(activity, LakehouseMaintenanceActivity.TABLE_NAME, typeProperties.table);
        ValueAdapter.toModel(activity, LakehouseMaintenanceActivity.OPTIMIZE_ENABLED, typeProperties.optimizeEnabled);
        ValueAdapter.toModel(activity, LakehouseMaintenanceActivity.VORDER_ENABLED, typeProperties.vOrderEnabled);
        ValueAdapter.toModel(activity, LakehouseMaintenanceActivity.ZORDER_ENABLED, typeProperties.zOrderEnabled);
        ValueAdapter.toModel(activity, LakehouseMaintenanceActivity.VACUUM_ENABLED, typeProperties.vacuumEnabled);
        ValueAdapter.toModel(activity, LakehouseMaintenanceActivity.RETENTION_PERIOD, typeProperties.retentionPeriod);
        ValueAdapter.toModel(activity, LakehouseMaintenanceActivity.ZORDER_ENABLED, typeProperties.zOrderEnabled);
        ValueAdapter.toModel(activity, LakehouseMaintenanceActivity.WAIT_ON_COMPLETION, typeProperties.waitOnCompletion);

        const zOrderEnabled = activity.zOrderEnabled || ValueAdapter.isTemplated(activity, LakehouseMaintenanceActivity.ZORDER_ENABLED);
        if (zOrderEnabled) {
            activity.zOrderColumns = typeProperties.zOrderColumns?.map((col) => {
                const param = Owned.create<ParameterConfig>(ParameterConfig.MODEL, activity);
                param.name = col;
                param.type = 'string';
                return param;
            });
        }

        if (modelActivity.linkedServiceName !== undefined) {
            (activity as LakehouseMaintenanceActivity).lakehouseLinkedService = ReferenceAdapter.toProxyModel<LakehouseLinkedService>(
                Metamodel.modelFor(LakehouseLinkedService),
                modelActivity.linkedServiceName,
                this.context
            );
        }

        const linkedServiceType = LakehouseLinkedService.MODEL.name;

        let linkedServiceModel: IModel = null;
        let linkedServiceName: Model.LinkedServiceReference = null;

        const connectionSettings = modelActivity.connectionSettings;

        if (!!connectionSettings) {
            const linkedServiceRegistryService = await getServiceInstance<LinkedServiceRegistryService>(
                this.context.contextData,
                'LinkedServiceRegistryService'
            );
            const linkedServiceMetadata =
                linkedServiceRegistryService.getLinkedServiceConfiguration(linkedServiceType);
            
            if (linkedServiceMetadata?.dmtsCredentialConnectionType) {
                if (isLogicAppExpression(connectionSettings?.properties?.linkedServiceName?.referenceName)) {
                    const model = Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType);
                    if (model) {
                        const templateLinkedService = Owned.create<LinkedService>(model, activity);
                        templateLinkedService.name = generateUUID().replaceAll('-', '_');
                        ValueAdapter.toModel(
                            templateLinkedService,
                            Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType).attribute(
                                'objectId'
                            ),
                            connectionSettings.properties.linkedServiceName.referenceName
                        );
                        templateLinkedService.parameterized = true;
                        activity.artifactDmtsCredential = templateLinkedService;
                    }
                    loggerService.log('lakehouse-maintenance-activity.adapter.ts', 'lakehouse credential is parameterized');
                } else {
                    activity.artifactDmtsCredential = ReferenceAdapter.toProxyModel(
                        Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType),
                        connectionSettings.properties.linkedServiceName,
                        this.context
                    );
                }
            }

            linkedServiceModel = Metamodel.modelByName(linkedServiceType);
            const linkedServiceAdapter = this.context.asyncAdapterFor(
                linkedServiceModel
            ) as LinkedServiceAdapter<LakehouseLinkedService>;

            const lsModel: LakehouseLinkedService = Owned.create<LakehouseLinkedService>(
                linkedServiceModel,
                activity
            );
            await linkedServiceAdapter?.toModel(connectionSettings, lsModel);
            if (lsModel?.parameterized || activity?.artifactDmtsCredential?.parameterized) {
                activity.lakehouseLinkedService = lsModel;
                loggerService.log('lakehouse-maintenance-activity.adapter.ts', 'lakehouse linked service is parameterized');
            } else {
                linkedServiceName = {
                    type: 'LinkedServiceReference',
                    referenceName: linkedServiceAdapter.getArtifactId(connectionSettings),
                    workspaceObjectId: linkedServiceAdapter.getWorkspaceObjectId(connectionSettings)
                };
            }
        }

        if (linkedServiceName != null) {
            activity.lakehouseLinkedService = ReferenceAdapter.toProxyModel<LakehouseLinkedService>(
                Metamodel.modelFor(LakehouseLinkedService),
                linkedServiceName,
                this.context
            );
        }
        
        return activity;
    }

}

